import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, Loader2, Save, AlertCircle, HelpCircle } from 'lucide-react';
import GapAnalysis from './GapAnalysis';
import Questionnaire from './Questionnaire';
import MaturitySelector from './MaturitySelector';
import CapabilitySuggestion from './CapabilitySuggestion';
import TransactionListModal from './TransactionListModal';
import { useSuggestions } from '../hooks/useSuggestions';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'gaps', label: 'Gap Analysis' },
  { id: 'questionnaire', label: 'Questionnaire' }
];

const STATUS_OPTIONS = ['NOT_STARTED', 'PLANNING', 'IN_PROGRESS', 'DEPLOYED'];
const PRIORITY_OPTIONS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

const AssessmentPanel = ({
  isOpen,
  onClose,
  department,
  framework,
  assessments,
  gaps,
  summary,
  loading,
  saving,
  onUpdateAssessment,
  onReload
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedPillar, setExpandedPillar] = useState(null);
  const [editingCapability, setEditingCapability] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Transaction modal state
  const [transactionModal, setTransactionModal] = useState({
    isOpen: false,
    capabilityCode: null,
    capabilityName: null,
    vendor: null,
    product: null
  });

  // Fetch capability suggestions based on procurement data
  const {
    getSuggestionForCapability,
    fetchTransactions
  } = useSuggestions(department?.department_id);

  const getAssessment = (capabilityId) => {
    return assessments.find(a => a.zt_capability_id === capabilityId);
  };

  const handleEditCapability = (capability) => {
    const existing = getAssessment(capability.zt_capability_id);
    setEditingCapability(capability);
    setEditForm({
      maturity_level: existing?.maturity_level || 'TRADITIONAL',
      implementation_status: existing?.implementation_status || 'NOT_STARTED',
      priority: existing?.priority || 'MEDIUM',
      current_solution: existing?.current_solution || ''
    });
  };

  const handleSave = async () => {
    if (!editingCapability) return;
    await onUpdateAssessment(editingCapability.zt_capability_id, editForm);
    setEditingCapability(null);
  };

  // Handle accepting a suggestion - auto-fill current_solution
  const handleAcceptSuggestion = (vendor, product) => {
    const solution = product ? `${vendor} ${product}` : vendor;
    setEditForm(f => ({ ...f, current_solution: solution }));
  };

  // Open transaction modal for a capability
  const openTransactionModal = (capability) => {
    const suggestion = getSuggestionForCapability(capability.capability_code);
    setTransactionModal({
      isOpen: true,
      capabilityCode: capability.capability_code,
      capabilityName: capability.capability_name,
      vendor: suggestion?.vendor || null,
      product: suggestion?.product || null
    });
  };

  // Close transaction modal
  const closeTransactionModal = () => {
    setTransactionModal({
      isOpen: false,
      capabilityCode: null,
      capabilityName: null,
      vendor: null,
      product: null
    });
  };

  const pillarScores = summary?.pillarScores || [];

  return (
    <div className={`assessment-panel ${isOpen ? 'assessment-panel--open' : ''}`}>
      {/* Panel Header */}
      <div className="assessment-panel-header">
        <div className="assessment-panel-title">
          <div className="assessment-panel-title-text">
            <h2>Assessment</h2>
            {department && (
              <span className="assessment-dept-name">
                {department.common_name || department.official_name}
              </span>
            )}
          </div>
          {saving && (
            <div className="assessment-saving">
              <Loader2 size={14} className="spin" />
              Saving...
            </div>
          )}
        </div>
        <button className="assessment-panel-close" onClick={onClose} aria-label="Close panel">
          <X size={18} />
        </button>
      </div>

      {/* Summary Bar */}
      {summary && (
        <div className="assessment-summary-bar">
          <div className="assessment-score">
            <span className="assessment-score-value">{Math.round(summary.overallScore || 0)}</span>
            <span className="assessment-score-label">Overall Score</span>
          </div>
          <div className="assessment-readiness">
            <span className="readiness-label">Readiness:</span>
            <span
              className={`readiness-level readiness-level--${(summary.readinessLevel || '').toLowerCase()}`}
            >
              {summary.readinessLevel || 'Unknown'}
            </span>
          </div>
          {summary.ab869Compliance !== undefined && (
            <div className="assessment-ab869">
              <span className="ab869-label">AB 869:</span>
              <span
                className="ab869-value"
                style={{ color: summary.ab869Compliance >= 80 ? 'var(--zt-strong)' : 'var(--zt-gap)' }}
              >
                {Math.round(summary.ab869Compliance)}%
              </span>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="assessment-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`assessment-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="assessment-panel-content">
        {loading ? (
          <div className="assessment-loading">
            <Loader2 size={24} className="spin" />
            <p>Loading assessment data...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="assessment-overview">
                {!department && (
                  <div className="assessment-no-dept">
                    <AlertCircle size={24} style={{ color: 'var(--color-text-muted)' }} />
                    <p>Select a department to view and edit assessments</p>
                  </div>
                )}

                {department && framework?.pillars && (
                  <div className="assessment-pillars">
                    {framework.pillars.map(pillar => {
                      const isExpanded = expandedPillar === pillar.pillar_code;
                      const pillarVar = `var(--zt-${pillar.pillar_code.toLowerCase()})`;
                      const pillarScore = pillarScores.find(ps => ps.pillar_code === pillar.pillar_code);
                      const assessedCount = (pillar.capabilities || []).filter(
                        c => getAssessment(c.zt_capability_id)
                      ).length;

                      return (
                        <div key={pillar.pillar_code} className="assessment-pillar-section">
                          <button
                            className="assessment-pillar-header"
                            onClick={() => setExpandedPillar(isExpanded ? null : pillar.pillar_code)}
                            style={{ borderLeftColor: pillarVar }}
                          >
                            <span className="assessment-pillar-name" style={{ color: pillarVar }}>
                              {pillar.pillar_name}
                            </span>
                            <div className="assessment-pillar-meta">
                              <span className="assessment-pillar-progress">
                                {assessedCount}/{(pillar.capabilities || []).length}
                              </span>
                              {pillarScore && (
                                <span
                                  className="assessment-pillar-score"
                                  style={{ color: pillarVar }}
                                >
                                  {Math.round(pillarScore.score)}%
                                </span>
                              )}
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="assessment-capabilities">
                              {(pillar.capabilities || []).map(cap => {
                                const existing = getAssessment(cap.zt_capability_id);
                                const isEditing = editingCapability?.zt_capability_id === cap.zt_capability_id;

                                return (
                                  <div
                                    key={cap.zt_capability_id}
                                    className={`assessment-cap-row ${isEditing ? 'editing' : ''}`}
                                  >
                                    <div className="assessment-cap-info">
                                      <span className="assessment-cap-name">{cap.capability_name}</span>
                                      {(cap.ab869_critical || cap.is_critical) && (
                                        <span className="capability-ab869-badge">AB 869</span>
                                      )}
                                    </div>

                                    {isEditing ? (
                                      <div className="assessment-cap-edit">
                                        {/* AI Suggestion Banner */}
                                        <CapabilitySuggestion
                                          suggestion={getSuggestionForCapability(cap.capability_code)}
                                          onAccept={handleAcceptSuggestion}
                                          onViewTransactions={() => openTransactionModal(cap)}
                                        />

                                        <MaturitySelector
                                          value={editForm.maturity_level}
                                          onChange={val => setEditForm(f => ({ ...f, maturity_level: val }))}
                                        />
                                        <div className="assessment-edit-row">
                                          <label>Status</label>
                                          <select
                                            value={editForm.implementation_status}
                                            onChange={e => setEditForm(f => ({ ...f, implementation_status: e.target.value }))}
                                            className="assessment-select"
                                          >
                                            {STATUS_OPTIONS.map(s => (
                                              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                                            ))}
                                          </select>
                                        </div>
                                        <div className="assessment-edit-row">
                                          <label>Priority</label>
                                          <select
                                            value={editForm.priority}
                                            onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}
                                            className="assessment-select"
                                          >
                                            {PRIORITY_OPTIONS.map(p => (
                                              <option key={p} value={p}>{p}</option>
                                            ))}
                                          </select>
                                        </div>
                                        <div className="assessment-edit-row">
                                          <label>Current Solution</label>
                                          <input
                                            type="text"
                                            value={editForm.current_solution}
                                            onChange={e => setEditForm(f => ({ ...f, current_solution: e.target.value }))}
                                            className="assessment-input"
                                            placeholder="e.g. CrowdStrike, Okta..."
                                          />
                                        </div>
                                        <div className="assessment-edit-actions">
                                          <button
                                            className="btn-secondary btn-sm"
                                            onClick={() => setEditingCapability(null)}
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            className="btn-primary btn-sm"
                                            onClick={handleSave}
                                            disabled={saving}
                                          >
                                            {saving ? <Loader2 size={12} className="spin" /> : <Save size={12} />}
                                            Save
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="assessment-cap-current">
                                        {existing ? (
                                          <>
                                            <span
                                              className={`maturity-chip maturity-chip--${(existing.maturity_level || '').toLowerCase()}`}
                                            >
                                              {existing.maturity_level || 'Not Set'}
                                            </span>
                                            {existing.implementation_status && (
                                              <span className={`status-chip status-chip--${existing.implementation_status.toLowerCase()}`}>
                                                {existing.implementation_status.replace(/_/g, ' ')}
                                              </span>
                                            )}
                                          </>
                                        ) : (
                                          <span className="assessment-not-assessed">Not assessed</span>
                                        )}
                                        <button
                                          className="assessment-edit-btn"
                                          onClick={() => handleEditCapability(cap)}
                                        >
                                          Edit
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Gap Analysis Tab */}
            {activeTab === 'gaps' && (
              <GapAnalysis gaps={gaps} loading={loading} />
            )}

            {/* Questionnaire Tab */}
            {activeTab === 'questionnaire' && (
              department ? (
                <Questionnaire
                  departmentId={department.department_id}
                  onComplete={onReload}
                />
              ) : (
                <div className="assessment-no-dept">
                  <HelpCircle size={24} style={{ color: 'var(--color-text-muted)' }} />
                  <p>Select a department to complete the questionnaire</p>
                </div>
              )
            )}
          </>
        )}
      </div>

      {/* Transaction List Modal */}
      <TransactionListModal
        isOpen={transactionModal.isOpen}
        onClose={closeTransactionModal}
        capabilityCode={transactionModal.capabilityCode}
        capabilityName={transactionModal.capabilityName}
        vendor={transactionModal.vendor}
        product={transactionModal.product}
        fetchTransactions={fetchTransactions}
      />
    </div>
  );
};

export default AssessmentPanel;
